import { memo, type Ref, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";

import { copyLastPackageEntry, deletePackageEntry } from "~/features/cart/cart/redux/actions/cartPageActions";
import { deleteProductSaga } from "~/features/cart/cart/redux/actions/cartQuoteCommonActions";
import type { CartEntryRowViewModel, ProductTableViewModel } from "~/features/cart/cartV2/models/ProductTableViewModel";
import ProductEntryList, { type ProductEntryListRef } from "~/features/cart/cartV2/ProductEntryList";

interface InsertAlternateRowInput {
  anchorCartEntryId: number | null;
  productCode: string;
  designation: string;
}

interface ProductTableProps {
  viewModel: ProductTableViewModel;
  onClear?: () => void;
  "data-test-id"?: string;
  scrollRef?: Ref<ProductEntryListRef>;
  onInsertAlternateRow?: (input: InsertAlternateRowInput) => void;
  onCloseAlternateRow?: (rowId: string) => void;
}

const ProductTable = (props: ProductTableProps) => {
  const { viewModel, "data-test-id": testId, onInsertAlternateRow, onCloseAlternateRow, scrollRef } = props;
  const dispatch = useDispatch();

  const entryPositionByCartEntryId = useMemo(
    () =>
      new Map(
        viewModel.rows
          .filter((row): row is CartEntryRowViewModel => row.kind === "entry")
          .map((row, index) => [row.cartEntryId, index]),
      ),
    [viewModel.rows],
  );
  const entryRowByCartEntryId = useMemo(
    () =>
      new Map(
        viewModel.rows
          .filter((row): row is CartEntryRowViewModel => row.kind === "entry")
          .map((row) => [row.cartEntryId, row]),
      ),
    [viewModel.rows],
  );

  const getAlternateInsertPosition = useCallback(
    (anchorCartEntryId: number | null) => {
      if (anchorCartEntryId === null) {
        return undefined;
      }

      return entryPositionByCartEntryId.get(anchorCartEntryId);
    },
    [entryPositionByCartEntryId],
  );

  const handleCloseAlternateRow = useCallback(
    (rowId: string) => {
      onCloseAlternateRow?.(rowId);
    },
    [onCloseAlternateRow],
  );

  const handleDeleteProduct = useCallback(
    (cartEntryId: number) => {
      const row = entryRowByCartEntryId.get(cartEntryId);

      if (!row?.mainDescriptor) {
        return;
      }

      onInsertAlternateRow?.({
        anchorCartEntryId: row.cartEntryId,
        productCode: row.mainDescriptor.base.code,
        designation: row.mainDescriptor.base.designation,
      });
      dispatch(deleteProductSaga(row.mainDescriptor.base.code));
    },
    [dispatch, entryRowByCartEntryId, onInsertAlternateRow],
  );

  const handleCopyLine = useCallback(
    (packEntryId: number) => {
      dispatch(copyLastPackageEntry(packEntryId));
    },
    [dispatch],
  );

  const handleDeleteLine = useCallback(
    ({ cartEntryId, lineItemId }: { cartEntryId: number; lineItemId: number }) => {
      const row = entryRowByCartEntryId.get(cartEntryId);
      const line = row?.details.lines.find((candidate) => candidate.lineItemId === lineItemId);
      const descriptor = line?.descriptor ?? row?.mainDescriptor;

      if (row && line && !line.flags.inStock && row.summary.canDeleteProduct && descriptor) {
        onInsertAlternateRow?.({
          anchorCartEntryId: row.cartEntryId,
          productCode: descriptor.base.code,
          designation: descriptor.base.designation,
        });
      }

      dispatch(deletePackageEntry(lineItemId));
    },
    [dispatch, entryRowByCartEntryId, onInsertAlternateRow],
  );

  return (
    <ProductEntryList
      rows={viewModel.rows}
      editMode={viewModel.mode === "edit"}
      scrollRef={scrollRef}
      data-test-id={testId}
      onCloseAlternateRow={handleCloseAlternateRow}
      onDeleteProduct={handleDeleteProduct}
      onDeleteLine={handleDeleteLine}
      onCopyLine={handleCopyLine}
      getAlternateInsertPosition={getAlternateInsertPosition}
    />
  );
};

export default memo(ProductTable);
